import { EventEmitter } from '../../../event-emitter';
import { ConnectStatus } from '../../interface/feature-flag-controller';
import type { FeatureFlagController } from '../../interface/feature-flag-controller';
import { FFS_FEATURE_FLAGS as RAW_FFS_FEATURE_FLAGS } from '../../../constants/feature-flags';
import FfsDelegate from './ffs-delegate';
import { FfsController as FfsControllerFromSdk } from '@ringcentral/feature-flag-service';
import type { FeatureFlagKey, FeatureFlag, FeatureFlagIdentifyInfo, FFSFetchFn } from '../../types';
import { FeatureFlagEvent, FeatureFlagSourceName } from '../../types';
import { FFS_PREFIX, FFS_FEATURE_KEY, FFS_IDENTIFY_INFO_KEY } from '../../constant';
import { readLocallyStored, writeLocallyStored } from '../../../local-storage';
import { equals } from 'ramda';
import { getPrefixedLogger, prefixes } from '../../../rwc-logger';
import { isApplication, OS_INFO } from '../../../client-detection';

const logger = getPrefixedLogger(prefixes.VC_UTILS, 'feature-flags_ffs');

/**
 * For the convenience of testing, a localstorage entry is left
 */
const MOCK_FFS_FEATURE_FLAGS = readLocallyStored('MOCK_FFS_FEATURE_FLAGS', {}, true);
const FFS_FEATURE_FLAGS = { ...RAW_FFS_FEATURE_FLAGS, ...MOCK_FFS_FEATURE_FLAGS };

/**
 * management of ffs flags, ffs is as a feature flag data source
 * init ffs, obverse ffs flags changes, and get flag by key
 * ffs sdk "feature-flag-service"(developed by jupiter) provides interaction with ffs server
 * @class
 */
class FfsController extends EventEmitter implements FeatureFlagController {
    private static instance: FfsController | null;
    private _ffsControllerFromSdk!: FfsControllerFromSdk;
    private _connectStatus: ConnectStatus = ConnectStatus.PENDING;
    private _ffsPublicServer = '';
    private _ffsAuthServer = '';
    private _brandId = '';

    // This is to record logs to investigate RCV-97742, it has no practical significance
    private _fetchedCount = 0;
    private _disposedCount = 0;

    private _identifyInfo: FeatureFlagIdentifyInfo | null = readLocallyStored(
        FFS_IDENTIFY_INFO_KEY,
        null,
        true
    );

    private constructor() {
        super();
    }
    public static getInstance() {
        if (!FfsController.instance) {
            FfsController.instance = new FfsController();
        }
        return FfsController.instance;
    }

    /**
     * build the user params for auth flags
     * these parameters are used to identify user information
     * ffs server will use the information to get the final flag value
     */
    private _buildParam() {
        let baseParam: { [custom: string]: string } = {
            host: window.location.host,
            brandId: this._brandId,
            osName: OS_INFO,
            rwcPlatform: isApplication() ? 'electron' : 'web',
            anonymous: 'true',
        };
        if (this._identifyInfo) {
            baseParam = {
                ...baseParam,
                companyId: `RC-${this._identifyInfo.accountId}`,
                email: this._identifyInfo.email || '',
                //Originally, 'user' rules were used on LD. After migration, user is defined as 'ld-user-key'
                'ld-user-key': `RC-${this._identifyInfo.extension}`,
                anonymous: 'false',
            };
        }
        return baseParam;
    }
    /**
     * trigger when ffs result changed after diff(did in ffs sdk)
     */
    private _onChanged = () => {
        this.emit(FeatureFlagEvent.CHANGED);
    };
    /**
     * trigger when ffs fetch finished
     */
    private _onFetched = (_: string, isSuccess: boolean, info?: string) => {
        this._fetchedCount += 1;

        if (!this.isReady) {
            if (isSuccess) {
                this._connectStatus = ConnectStatus.SUCCESS;
                this.emit(FeatureFlagEvent.SUCCEED);
            } else {
                this._connectStatus = ConnectStatus.FAILED;
                this.emit(FeatureFlagEvent.FAILED);
            }
        }

        if (!isSuccess) {
            // throw error when fetch ffs failed, will be catched by sentry
            throw new Error(
                `feature-flags_ffs:fetch ffs failed,error:${info},isReady:${this.isReady}`
            );
        }
    };

    /**
     * add event listener
     */
    private _initListeners() {
        this._ffsControllerFromSdk.observeAllFlags(this._onChanged);
        this._ffsControllerFromSdk.observeFetchRequest(this._onFetched);
    }
    /**
     * remove event listener
     */
    private _removeListeners() {
        this._ffsControllerFromSdk.unobserveAllFlags(this._onChanged);
        this._ffsControllerFromSdk.unobserveFetchRequest(this._onFetched);
    }

    /**
     * init ffs controller and add event listeners
     * @param sendFetch
     * @param brandId
     */
    public init(sendFetch: FFSFetchFn, brandId: string) {
        this._brandId = brandId;
        if (!sendFetch) {
            logger.error('sendFetch is undefined');
            return;
        }
        if (this._ffsControllerFromSdk) {
            this.dispose();
        }
        this._connectStatus = ConnectStatus.PENDING;
        const ffsDelegate = new FfsDelegate(sendFetch);
        this._ffsControllerFromSdk = new FfsControllerFromSdk(
            0,
            {},
            ffsDelegate,
            FFS_FEATURE_KEY,
            Object.assign(logger, { trace: logger.info.bind(logger) })
        );
        this._initListeners();
    }

    public dispose() {
        this._disposedCount += 1;
        this._removeListeners();
    }
    /**
     * set ffs public server and start polling ffs public flag
     * the ffs public server is gotten from discovery initial function
     * for RCV now, since user has guesttoken bufore login, so we can fetch auth flags, no need to fetch public flag
     * so this function not used yet
     * @param server ffs public server
     */
    public setFfsPublicServer(server: string) {
        if (!server) {
            logger.error('ffs public server is undefined');
            return;
        }
        if (
            this._ffsPublicServer !== server &&
            !this._ffsControllerFromSdk.isPublicReady.isOpen()
        ) {
            this.startPollingPublicConfig(server);
        }
        // set the new address to the ffs sdk, which will take effect in next polling.
        this._ffsControllerFromSdk.setFfsPublicServer(server);
        this._ffsPublicServer = server;
    }
    /**
     * set ffs public server and start polling ffs auth flag
     * the ffs public server is gotten from discovery external function
     * @param server ffs auth server
     */
    public setFfsAuthServer(server: string) {
        if (!server) {
            logger.error('ffs auth server is unavailable');
            return;
        }
        if (this._ffsAuthServer !== server && !this._ffsControllerFromSdk.isAuthReady.isOpen()) {
            this.startPollingAuthConfig(server);
        }
        this._ffsControllerFromSdk.setFfsAuthServer(server);
        this._ffsAuthServer = server;
    }
    /**
     * start polling public config
     * call the ffs sdk to fetch and polling ffs flags
     * @param server
     */
    public startPollingPublicConfig(server = this._ffsPublicServer) {
        this._ffsControllerFromSdk.startPollingFfsPublicConfig(
            server,
            {
                flags: [`${FFS_PREFIX}.*`],
                parameters: {
                    BrandId: this._brandId,
                },
            },
            true
        );
    }
    /**
     * start polling auth config
     * call the ffs sdk to fetch and polling ffs flags
     * @param server
     */
    public startPollingAuthConfig(server = this._ffsAuthServer) {
        this._ffsControllerFromSdk.startPollingFfsAuthConfig(
            server,
            {
                flags: [`${FFS_PREFIX}.*`],
                parameters: this._buildParam(),
            },
            false,
            undefined,
            true
        );
    }
    public get sourceName() {
        return FeatureFlagSourceName.FFS;
    }
    /**
     * whether the ffs data is ready
     */
    public get isReady() {
        return this._connectStatus === ConnectStatus.SUCCESS;
    }
    /**
     * whether the ffs init finished(success or failed)
     */
    public get isPending() {
        return this._connectStatus === ConnectStatus.PENDING;
    }

    public get isFailed() {
        return this._connectStatus === ConnectStatus.FAILED;
    }
    /**
     * get feature flag value by key
     * @param type
     */
    public getFeatureFlag(type: FeatureFlagKey): FeatureFlag {
        const ffsFlagKey = FFS_FEATURE_FLAGS[type];
        if (!this.isReady || !ffsFlagKey) {
            return null;
        }
        return this._ffsControllerFromSdk.getFlagByKey(ffsFlagKey) as FeatureFlag;
    }
    /**
     * get the origin key in ffs, FFS_FEATURE_FLAGS is a maptable of feature flag key and ffs key
     * @param type
     */
    public getFeatureFlagOriginKey(type: FeatureFlagKey): string {
        return FFS_FEATURE_FLAGS[type] || type;
    }
    /**
     * only the key that appears in FFS_FEATURE_FLAGS will take the value from ffs
     * @param type
     */
    public isFlagSupported(type: FeatureFlagKey) {
        return Object.prototype.hasOwnProperty.call(FFS_FEATURE_FLAGS, type);
    }
    /**
     * clear the cache and refecth the ffs flags after user logined or logout
     * @param identifyInfo
     * @param callback
     */
    public identify(identifyInfo: FeatureFlagIdentifyInfo | null) {
        if (!this._ffsAuthServer) {
            logger.error('ffs auth server is unavailable');
            return;
        }
        if (!equals(this._identifyInfo, identifyInfo)) {
            this._identifyInfo = identifyInfo;
            writeLocallyStored(FFS_IDENTIFY_INFO_KEY, identifyInfo, true);
            this.startPollingAuthConfig();
        }
    }
}
export default FfsController;
